/**
 * Client-side dependency resolver.
 * Checks requires/excludes/includes_price rules for selected options.
 */

/**
 * Validate current selection against dependencies.
 *
 * @param {number[]} selectedIds - Currently selected option IDs.
 * @param {Object} dependencies - Grouped by option_id: { [optionId]: [{ depends_on_option_id, dependency_type }] }
 * @param {Object} optionMap - Map of option_id to option data (name, etc.)
 * @returns {{ valid: boolean, errors: Array, warnings: Array }}
 */
export function validateSelection(selectedIds, dependencies, optionMap = {}) {
    const selectedSet = new Set(selectedIds);
    const errors = [];
    const warnings = [];

    for (const optionId of selectedIds) {
        const deps = dependencies[optionId];
        if (!deps) continue;

        for (const dep of deps) {
            const depId = Number(dep.depends_on_option_id);
            const type = dep.dependency_type;
            const optName = optionMap[optionId]?.name || `Optie #${optionId}`;
            const depName = optionMap[depId]?.name || `Optie #${depId}`;

            if (type === 'requires' && !selectedSet.has(depId)) {
                errors.push({
                    type: 'missing_requirement',
                    optionId,
                    requires: depId,
                    message: `"${optName}" vereist "${depName}".`,
                });
            }

            if (type === 'excludes' && selectedSet.has(depId)) {
                errors.push({
                    type: 'exclusion_conflict',
                    optionId,
                    excludes: depId,
                    message: `"${optName}" kan niet samen met "${depName}".`,
                });
            }

            if (type === 'includes_price' && selectedSet.has(depId)) {
                warnings.push({
                    type: 'price_included',
                    optionId,
                    includes: depId,
                    message: `Prijs van "${depName}" is inbegrepen bij "${optName}".`,
                });
            }
        }
    }

    return { valid: errors.length === 0, errors, warnings };
}

/**
 * Get option IDs that are excluded by current selection.
 */
export function getExcludedOptions(selectedIds, dependencies) {
    const excluded = new Set();

    for (const optionId of selectedIds) {
        const deps = dependencies[optionId];
        if (!deps) continue;

        for (const dep of deps) {
            if (dep.dependency_type === 'excludes') {
                excluded.add(Number(dep.depends_on_option_id));
            }
        }
    }

    return excluded;
}

/**
 * Get option IDs whose price is included by current selection.
 */
export function getIncludedPriceOptions(selectedIds, dependencies) {
    const included = new Set();

    for (const optionId of selectedIds) {
        const deps = dependencies[optionId];
        if (!deps) continue;

        for (const dep of deps) {
            if (dep.dependency_type === 'includes_price') {
                included.add(Number(dep.depends_on_option_id));
            }
        }
    }

    return included;
}

/**
 * Get missing required options for current selection.
 */
export function getMissingRequirements(selectedIds, dependencies) {
    const selectedSet = new Set(selectedIds);
    const missing = new Set();

    for (const optionId of selectedIds) {
        const deps = dependencies[optionId];
        if (!deps) continue;

        for (const dep of deps) {
            if (dep.dependency_type === 'requires' && !selectedSet.has(Number(dep.depends_on_option_id))) {
                missing.add(Number(dep.depends_on_option_id));
            }
        }
    }

    return missing;
}
